"use strict";

(function () {
	const ANIMATION_EVENTS =
		"webkitAnimationEnd mozAnimationEnd MSAnimationEnd oanimationend animationend"
			.split(" ")
			.filter(Boolean);

	const popupState = {
		sPrevious: window.scrollY,
		sDirection: "down",
		globalEventsBound: false,
	};

	const toNumber = (value, fallback = 0) => {
		const parsed = Number.parseInt(value, 10);
		return Number.isFinite(parsed) ? parsed : fallback;
	};

	const getPopup = (target) => {
		if (!target) return null;
		if (target instanceof Element && target.classList.contains("popup")) return target;
		if (target instanceof Element) return target.closest(".popup");
		return null;
	};

	const getCookie = (name) => {
		const matches = document.cookie.match(
			new RegExp(
				"(?:^|; )" + name.replace(/([\.$?*|{}\(\)\[\]\\\/\+^])/g, "\\$1") + "=([^;]*)"
			)
		);
		return matches ? decodeURIComponent(matches[1]) : undefined;
	};

	const setCookie = (name, value, options = {}) => {
		const normalizedOptions = { path: "/", ...options };
		const expiresDays = toNumber(normalizedOptions.expires, 0);

		if (expiresDays) {
			normalizedOptions.expires = new Date(
				Date.now() + expiresDays * 24 * 60 * 60 * 1000
			).toUTCString();
		} else {
			delete normalizedOptions.expires;
		}

		let updatedCookie = `${name}=${encodeURIComponent(value)}`;
		Object.entries(normalizedOptions).forEach(([propName, propValue]) => {
			updatedCookie += `; ${propName}`;
			if (propValue !== true) {
				updatedCookie += `=${propValue}`;
			}
		});

		document.cookie = updatedCookie;
	};

	const getScrollPercent = () => {
		const h = document.documentElement;
		const b = document.body;
		const scrollTop = h.scrollTop || b.scrollTop;
		const scrollHeight = h.scrollHeight || b.scrollHeight;
		if (scrollHeight <= h.clientHeight) return 0;
		return (scrollTop / (scrollHeight - h.clientHeight)) * 100;
	};

	const onAnimationEnd = (element, callback) => {
		if (!element) {
			if (typeof callback === "function") callback();
			return;
		}

		let called = false;
		const handler = () => {
			if (called) return;
			called = true;
			ANIMATION_EVENTS.forEach((eventName) =>
				element.removeEventListener(eventName, handler)
			);
			if (typeof callback === "function") callback();
		};

		ANIMATION_EVENTS.forEach((eventName) =>
			element.addEventListener(eventName, handler, { once: true })
		);
	};

	const applyAnimation = (popup, name, callback) => {
		if (!popup) return;
		const overlayName =
			typeof callback === "function" ? "popupExitFade" : "popupOpenFade";

		const overlay = popup.nextElementSibling?.classList.contains("popup-overlay")
			? popup.nextElementSibling
			: null;
		const popupWrap = popup.querySelector(".popup-wrap");

		if (overlay) {
			overlay.classList.add("popup-animated", overlayName);
			onAnimationEnd(overlay, () => {
				overlay.classList.remove("popup-animated", overlayName);
			});
		}

		if (popupWrap) {
			if (name) popupWrap.classList.add("popup-animated", name);
			onAnimationEnd(popupWrap, () => {
				if (name) popupWrap.classList.remove("popup-animated", name);
				if (typeof callback === "function") callback();
			});
		} else if (typeof callback === "function") {
			callback();
		}
	};

	const isAllowPopup = (popup) => {
		const popupId = popup?.dataset.id;
		const limitDisplay = toNumber(popup?.dataset.limitDisplay, 0);
		const limitDisplayCookie = toNumber(getCookie(`popup-${popupId}`), 0);
		if (limitDisplay && limitDisplayCookie && limitDisplayCookie >= limitDisplay) {
			return false;
		}
		return true;
	};

	const closePopup = (target) => {
		const popup = getPopup(target);
		if (!popup) return;
		const animation = popup.dataset.exitAnimation;

		applyAnimation(popup, animation, () => {
			popup.classList.add("popup-already-opened");
			popup.classList.remove("popup-open");
			document.body.classList.remove("popup-scroll-hidden");
		});
	};

	const closeTriggerPopup = (popup) => {
		if (!popup || popup.dataset.closeTriggerBound === "true") return;
		popup.dataset.closeTriggerBound = "true";

		const trigger = popup.dataset.closeTrigger;
		if (trigger === "delay") {
			const delayMs = toNumber(popup.dataset.closeDelayNumber, 0) * 1000;
			window.setTimeout(() => closePopup(popup), delayMs);
		}

		if (trigger === "scroll") {
			const pointScrollType = popup.dataset.closeScrollType;
			const pointScrollPosition = toNumber(popup.dataset.closeScrollPosition, 0);
			const initScrollPx = toNumber(popup.dataset.initScrollPx, 0);
			const initScrollPercent = Number(popup.dataset.initScrollPercent || 0);

			const onScroll = () => {
				if (pointScrollType === "px") {
					if (
						popupState.sDirection === "up" &&
						window.scrollY < initScrollPx - pointScrollPosition
					) {
						closePopup(popup);
					}
					if (
						popupState.sDirection === "down" &&
						window.scrollY >= initScrollPx + pointScrollPosition
					) {
						closePopup(popup);
					}
				}

				if (pointScrollType === "%") {
					if (
						popupState.sDirection === "up" &&
						getScrollPercent() < initScrollPercent - pointScrollPosition
					) {
						closePopup(popup);
					}
					if (
						popupState.sDirection === "down" &&
						getScrollPercent() >= initScrollPercent + pointScrollPosition
					) {
						closePopup(popup);
					}
				}
			};

			document.addEventListener("scroll", onScroll, { passive: true });
		}
	};

	const openPopup = (target) => {
		const popup = getPopup(target);
		if (!popup) return;

		if (popup.dataset.bodyScrollDisable === "true") {
			document.body.classList.add("popup-scroll-hidden");
		}

		const popupId = popup.dataset.id;
		const limit = toNumber(getCookie(`popup-${popupId}`), 0) + 1;
		setCookie(`popup-${popupId}`, limit, {
			expires: toNumber(popup.dataset.limitLifetime, 0),
		});

		if (popup.classList.contains("popup-open")) return;
		if (popup.classList.contains("popup-already-opened")) return;

		popup.classList.add("popup-open");
		popup.dataset.initScrollPx = String(window.scrollY);
		popup.dataset.initScrollPercent = String(getScrollPercent());

		applyAnimation(popup, popup.dataset.openAnimation);
		closeTriggerPopup(popup);
	};

	const agePopup = (target) => {
		const popup = getPopup(target);
		if (!popup) return;
		setCookie(`popup-age-${popup.dataset.id}`, 1, { expires: 360 });
	};

	const ageDeclined = () => {
		document
			.querySelectorAll(".age-verification__question")
			.forEach((el) => el.classList.remove("show"));
		document
			.querySelectorAll(".age-verification__declined")
			.forEach((el) => el.classList.add("show"));
	};

	const acceptPopup = (target) => {
		const popup = getPopup(target);
		if (!popup) return;
		setCookie(`popup-accept-${popup.dataset.id}`, 1, { expires: 360 });
	};

	const openTriggerPopup = (popup) => {
		const trigger = popup.dataset.openTrigger;

		if (trigger === "none") {
			const ageVerification = getCookie(`popup-age-${popup.dataset.id}` || "0");
			if (!ageVerification) openPopup(popup);
		}

		if (trigger === "delay") {
			const delayMs = toNumber(popup.dataset.openDelayNumber, 0) * 1000;
			window.setTimeout(() => openPopup(popup), delayMs);
		}

		if (trigger === "exit" && popup.dataset.exitBound !== "true") {
			popup.dataset.exitBound = "true";
			let showExit = true;
			document.addEventListener("mousemove", (event) => {
				const scroll =
					window.pageYOffset || document.documentElement.scrollTop || 0;
				if (event.pageY - scroll < 7 && showExit) {
					openPopup(popup);
					showExit = false;
				}
			});
		}

		if (trigger === "scroll" && popup.dataset.openScrollBound !== "true") {
			popup.dataset.openScrollBound = "true";
			const pointScrollType = popup.dataset.openScrollType;
			const pointScrollPosition = toNumber(popup.dataset.openScrollPosition, 0);

			const onScroll = () => {
				if (pointScrollType === "px" && window.scrollY >= pointScrollPosition) {
					openPopup(popup);
				}

				if (pointScrollType === "%" && getScrollPercent() >= pointScrollPosition) {
					openPopup(popup);
				}
			};
			document.addEventListener("scroll", onScroll, { passive: true });
		}

		if (trigger === "accept") {
			const accepted = getCookie(`popup-accept-${popup.dataset.id}` || "0");
			if (!accepted) openPopup(popup);
		}
	};

	const popupInit = () => {
		document.querySelectorAll(".popup").forEach((popup) => {
			if (popup.dataset.openTrigger === "manual") {
				const selector = popup.dataset.openManualSelector;
				if (selector && popup.dataset.manualBound !== "true") {
					popup.dataset.manualBound = "true";
					document.querySelectorAll(selector).forEach((triggerEl) => {
						triggerEl.classList.add("popup-trigger");
						triggerEl.addEventListener("click", (event) => {
							event.preventDefault();
							popup.classList.remove("popup-already-opened");
							openPopup(popup);
							if (event.currentTarget.classList.contains("popup")) {
								closePopup(event.currentTarget);
							}
						});
					});
				}
			}

			if (!isAllowPopup(popup) && !popup.classList.contains("age-verification")) {
				return;
			}

			openTriggerPopup(popup);
		});
	};

	const bindGlobalEvents = () => {
		if (popupState.globalEventsBound) return;
		popupState.globalEventsBound = true;

		document.addEventListener("click", (event) => {
			const closeBtn = event.target.closest(".popup-close");
			if (closeBtn) closePopup(closeBtn);

			const acceptBtn = event.target.closest(".popup-accept");
			if (acceptBtn) {
				acceptPopup(acceptBtn);
				closePopup(acceptBtn);
			}

			const ageCloseBtn = event.target.closest(".age-verification__popup-close");
			if (ageCloseBtn) {
				agePopup(ageCloseBtn);
				closePopup(ageCloseBtn);
			}

			if (event.target.closest(".age-verification__button-no")) {
				ageDeclined();
			}

			if (event.target.closest(".popup-overlay")) {
				document
					.querySelectorAll('.popup-open[data-overlay-close="true"]')
					.forEach((popup) => closePopup(popup));
			}
		});

		document.addEventListener("keyup", (event) => {
			if (event.key === "Escape") {
				document
					.querySelectorAll('.popup-open[data-esc-close="true"]')
					.forEach((popup) => closePopup(popup));
			}
			if (event.key === "F4") {
				document
					.querySelectorAll('.popup-open[data-f4-close="true"]')
					.forEach((popup) => closePopup(popup));
			}
		});

		document.addEventListener(
			"scroll",
			() => {
				const scrollCurrent = window.scrollY;
				popupState.sDirection =
					scrollCurrent > popupState.sPrevious ? "down" : "up";
				popupState.sPrevious = scrollCurrent;
			},
			{ passive: true }
		);
	};

	const init = () => {
		bindGlobalEvents();
		popupInit();
	};

	document.addEventListener("DOMContentLoaded", init);
	document.addEventListener("shopify:section:load", init);
	document.addEventListener("shopify:section:unload", () => {
		document.body.classList.remove("popup-scroll-hidden");
	});
})();
